/**
 * WaiversList - Displays a list of waiver/policy items
 *
 * @example
 * ```tsx
 * <WaiversList waivers={waivers} />
 * <WaiversList waivers={waivers} allowSign userEmail="user@example.com" />
 * ```
 */

"use client";

import { Box, Divider, Flex } from "@chakra-ui/react";
import { useState, useRef, useEffect } from "react";
import { MdCheckCircle, MdSchedule } from "react-icons/md";
import type { GetPolicyResponse } from "@/openapi/generated";
import { PLACEHOLDER_WAIVER_POLICY_ID } from "@/lib/constants/placeholder-waiver";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Badge, Button, Heading, Text } from "@/components/ui";
import { waiverAnchorId } from "./TermsTableOfContents";
import { WaiverSignPanel } from "./WaiverSignPanel";

export interface WaiversListProps {
  waivers: GetPolicyResponse[];
  showStatus?: boolean;
  allowSign?: boolean;
  userEmail?: string;
  /** Offset when jumping to a waiver anchor (member portal uses dashboard header height). */
  scrollMarginTop?: string;
  variant?: "public" | "portal";
}

const MAX_LINES_PUBLIC = 10;
const MAX_LINES_PORTAL = 8;

export const WaiversList = ({
  waivers,
  showStatus = true,
  allowSign = false,
  userEmail,
  scrollMarginTop = "96px",
  variant = "portal",
}: WaiversListProps) => {
  const theme = useThemeTokens();
  const isPublicLayout = variant === "public";
  const maxLines = isPublicLayout ? MAX_LINES_PUBLIC : MAX_LINES_PORTAL;
  const [expandedWaivers, setExpandedWaivers] = useState<Set<number>>(new Set());
  const [needsExpansion, setNeedsExpansion] = useState<Set<number>>(new Set());
  const textRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const toggleWaiver = (index: number) => {
    setExpandedWaivers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  useEffect(() => {
    const checkOverflow = () => {
      const newNeedsExpansion = new Set<number>();
      waivers.forEach((waiver, index) => {
        const element = textRefs.current[index];
        if (element && waiver.displayText) {
          const tempSpan = document.createElement("span");
          tempSpan.style.cssText = `
            position: absolute;
            visibility: hidden;
            display: block;
            white-space: pre-line;
            width: ${element.offsetWidth}px;
            font-size: ${getComputedStyle(element).fontSize};
            line-height: 1.75;
            font-family: ${getComputedStyle(element).fontFamily};
          `;
          tempSpan.textContent = waiver.displayText;
          document.body.appendChild(tempSpan);
          const fullHeight = tempSpan.scrollHeight;
          document.body.removeChild(tempSpan);

          const fontSize = parseFloat(getComputedStyle(element).fontSize);
          const lineHeight = fontSize * 1.75;
          const maxHeight = lineHeight * maxLines;

          if (fullHeight > maxHeight + 10) {
            newNeedsExpansion.add(index);
          }
        }
      });
      setNeedsExpansion(newNeedsExpansion);
    };

    setTimeout(checkOverflow, 100);
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [waivers, maxLines]);

  if (!waivers?.length) return null;

  const renderBodyText = (
    waiver: GetPolicyResponse,
    index: number,
    isExpanded: boolean,
    canSign: boolean,
  ) => (
    <Box
      ref={(el) => {
        textRefs.current[index] = el;
      }}
      maxH={canSign ? "320px" : undefined}
      overflowY={canSign ? "auto" : undefined}
      css={
        canSign
          ? {
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                background: theme.borderGlass.default,
                borderRadius: "4px",
              },
            }
          : undefined
      }
    >
      <Text
        variant="secondary"
        fontSize={isPublicLayout ? "md" : { base: "sm", md: "md" }}
        lineHeight="1.75"
        whiteSpace={isExpanded || canSign ? "pre-line" : "normal"}
        sx={
          isExpanded || canSign
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }
        }
      >
        {waiver.displayText}
      </Text>
    </Box>
  );

  const renderShowMore = (index: number, isExpanded: boolean, canSign: boolean) => {
    if (!needsExpansion.has(index) || canSign) return null;

    if (isPublicLayout) {
      return (
        <Text
          as="button"
          mt={4}
          bg="transparent"
          border="none"
          p={0}
          fontSize="sm"
          fontWeight="500"
          color={theme.accent.primary}
          cursor="pointer"
          transition="opacity 0.2s ease"
          onClick={() => toggleWaiver(index)}
          sx={{
            "@media (hover: hover)": {
              "&:hover": { opacity: 0.85, textDecoration: "underline" },
            },
          }}
        >
          {isExpanded ? "Show less" : "Show more"}
        </Text>
      );
    }

    return (
      <Button
        variant="ghost"
        size={{ base: "xs", md: "sm" }}
        mt={3}
        onClick={() => toggleWaiver(index)}
        color={theme.accent.primary}
        sx={{
          "@media (hover: hover)": {
            "&:hover": {
              bg: theme.accent.primaryBg,
            },
          },
        }}
      >
        {isExpanded ? "Show less" : "Show more"}
      </Button>
    );
  };

  if (isPublicLayout) {
    return (
      <Box borderTopWidth="1px" borderColor={theme.borderGlass.subtle}>
        {waivers.map((waiver, index) => {
          const { policyId, name } = waiver;
          const isExpanded = expandedWaivers.has(index);
          const isLast = index === waivers.length - 1;

          return (
            <Box
              key={String(policyId)}
              id={waiverAnchorId(policyId)}
              scrollMarginTop={scrollMarginTop}
              py={{ base: 6, md: 8 }}
              borderBottomWidth={isLast ? 0 : "1px"}
              borderColor={theme.borderGlass.subtle}
            >
              <Text
                fontSize="xs"
                fontWeight="600"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color={theme.text.tertiary}
                mb={2}
              >
                Policy
              </Text>
              <Text
                as="h2"
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="600"
                letterSpacing="-0.02em"
                color={theme.text.primary}
                lineHeight="1.25"
                mb={5}
              >
                {name}
              </Text>
              {renderBodyText(waiver, index, isExpanded, false)}
              {renderShowMore(index, isExpanded, false)}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Flex direction="column" rowGap={6}>
      {waivers.map((waiver, index) => {
        const { policyId, name, acceptedAt } = waiver;
        const isExpanded = expandedWaivers.has(index);
        const showButton = needsExpansion.has(index);
        const isAccepted = acceptedAt !== null && acceptedAt !== undefined;
        const canSign =
          allowSign &&
          !isAccepted &&
          userEmail &&
          policyId !== PLACEHOLDER_WAIVER_POLICY_ID;

        return (
          <Box
            key={String(policyId)}
            id={waiverAnchorId(policyId)}
            scrollMarginTop={scrollMarginTop}
            p={6}
            borderRadius="8px"
            borderWidth="1px"
            bg="none"
            borderColor={theme.borderGlass.default}
            boxShadow={theme.shadow.card}
          >
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "start" }}
              mb={4}
              gap={{ base: 2, md: 4 }}
            >
              <Heading
                size={{ base: "md", md: "lg" }}
                w="100%"
                flex={{ md: 1 }}
                minW={0}
                color={theme.text.primary}
              >
                {name}
              </Heading>
              {showStatus && isAccepted && (
                <Badge
                  tone="success"
                  fontSize="xs"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  px={2}
                  py={1}
                  flexShrink={0}
                  w="fit-content"
                >
                  <MdCheckCircle size={12} />
                  Signed
                </Badge>
              )}
              {showStatus && !isAccepted && (
                <Badge
                  tone="info"
                  fontSize="xs"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  px={2}
                  py={1}
                  flexShrink={0}
                  w="fit-content"
                >
                  <MdSchedule size={12} />
                  Pending signature
                </Badge>
              )}
            </Flex>
            {showStatus && isAccepted && acceptedAt && (
              <Flex align="center" gap={2} mb={4} fontSize="sm" color={theme.text.secondary}>
                <Text>
                  Accepted on{" "}
                  {new Date(acceptedAt).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Text>
              </Flex>
            )}
            <Divider mb={4} borderColor={theme.borderGlass.default} />
            {renderBodyText(waiver, index, isExpanded, Boolean(canSign))}
            {showButton && !canSign ? renderShowMore(index, isExpanded, false) : null}
            {canSign && userEmail ? (
              <WaiverSignPanel
                waiver={waiver}
                userEmail={userEmail}
                textRefs={textRefs}
                waiverIndex={index}
              />
            ) : null}
          </Box>
        );
      })}
    </Flex>
  );
};
